/**
 * Inhaltsverzeichnis
 * 	1. Menu
 * 		1.1 Properties
 * 		1.2 Constructor
 * 		1.3 
 */


// ==================================================
// MARK: 0. Setup
// --------------------------------------------------
// #=#=#=#=#=# 0.1 Imports #=#=#=#=#=#
import { BreakpointObserver } from '../../ts/class/observer/exportlist';
import { CSSRuleSet } from '../../ts/class/style/exportlist';
import style from './style.scss?inline';


// #=#=#=#=#=# 0.2 Types #=#=#=#=#=#


// ==================================================
// MARK: 1. Class
// --------------------------------------------------
class MenuElement	extends HTMLElement {
	// #=#=#=#=#=# 1.1 Properties #=#=#=#=#=#
		// #==== Static ====#
	/** Name of the Attribute to track the open state */
	private static openAttribute = "data-open";
	/** Backdrop Element */
	private static backdrop		= document.getElementById("cvh--backdrop");


		// #==== Initialised ====#
	/** Shadow DOM */
	private shadow: ShadowRoot		=	this.attachShadow({mode: 'open'});
	/**	Stylesheet */
	private stylesheet: CSSRuleSet	=	new CSSRuleSet(style);
	/** Bound Close function */
	private boundClose = this.close.bind(this);
	/** Soft Dismiss for ESC Key */
	private softDismissESC = (event: KeyboardEvent) => {
		if (event.key === 'Escape') {
			this.boundClose();
		}
	};


		// #==== Uninitialised ====#
	/**	Modal Element */
	private subelements: {
		button: HTMLButtonElement,
		modal: HTMLDivElement
	};


	// #=#=#=#=#=# 1.2 Constructor #=#=#=#=#=#
	constructor() {
		// #==== Parent ====#
		super();


		// #==== Properties ====#
		this.shadow.adoptedStyleSheets	=	[this.stylesheet.getStylesheet()];


		// #==== Mehtods ====#
		this.build();
	}


	// #=#=#=#=#=# 1.? Build Shadow Root #=#=#=#=#=#
	/**
	 * Builds the Shadow DOM
	 */
	private build(
	): void {
		this.shadow.innerHTML	=	`
			<div part="popover" hidden>
				<slot></slot>
			</div>
			<button part="toggle">
				<span class="bars"></span>
				<span class="sr-only">Toggle Menu</span>
			</button>
		`;
	}


	// #=#=#=#=#=# 1.? Connected Callback #=#=#=#=#=#
	/**
	 * Lifecycle Method: Connected Callback
	 */
	connectedCallback() {
		// #==== Set up Shadow Interaction ====#
		this.getElements();
		this.enqueueObserver();


		// #==== Event Listeners ====#
		this.subelements.button.onclick = this.toggleDialog.bind(this);
	}


	// #=#=#=#=#=# 1.? Get sub-elements #=#=#=#=#=#
	/**
	 * Stores the important subelements in the subelements object
	 */
	private getElements(
	): void {
		this.subelements = {
			button: this.shadow.querySelector('button') as HTMLButtonElement,
			modal: this.shadow.querySelector('div[hidden]') as HTMLDivElement
		};
	}


	// #=#=#=#=#=# 1.? Enqueue Observer #=#=#=#=#=#
	private enqueueObserver() {
		// #==== Fetch Config ====#
		let breakpoints	= [
			{
				range: parseInt(this.getAttribute('breakpoint')?? "768")
			}
		];


		// #==== Get Breakpoints ====#
		new BreakpointObserver(breakpoints, {
			element: this,
			modal: this.subelements.modal,
			onWithin: MenuElement.onWithin,
			onOutside: MenuElement.onOutside
		});
	}


	// #=#=#=#=#=# 1.? Event Handler: Outside Range #=#=#=#=#=#
	/**
	 * Sets the Element to a state for Desktop
	 */
	private static onOutside(
		this: BreakpointObserver
	): void {
		// #════ Get Properties ════#
		const element		=	this.getProperty('element') as MenuElement;
		const modal			=	this.getProperty('modal') as HTMLDivElement;


		// #════ Set State Properties ════#
		// Set Layout Type to inline
		element.setAttribute('layout', 'inline');

		// Close modal Type if open
		element.boundClose();

		// Show modal element
		modal.removeAttribute("hidden");
	}


	// #=#=#=#=#=# 1.? Event Handler: Within Range #=#=#=#=#=#
	/**
	 * Sets the Element to a state for Mobile
	 */
	private static onWithin(
		this: BreakpointObserver
	): void {
		// #════ Get Properties ════#
		const element		=	this.getProperty('element') as MenuElement;
		const modal			=	this.getProperty('modal') as HTMLDivElement;


		// #════ Set State Properties ════#
		// Set Layout Type to overlay
		element.setAttribute('layout', 'overlay');

		// Hide modal element
		modal.setAttribute("hidden", "");
	}


	// #=#=#=#=#=# 1.? Event Handler: Button Interaction #=#=#=#=#=#
	/**
	 * Toggles the Dialogs visibility
	 */
	private toggleDialog(
	): void {
		// #==== Hide ====#
		if(this.hasAttribute(MenuElement.openAttribute)) {
			this.close();
		}


		// #==== Show ====#
		else {
			this.open();
		}
	}


	// #=#=#=#=#=# 1.? Event Open #=#=#=#=#=#
	/**
	 * Opens the Dialog
	 */
	public open() {
		// #════ Remove Hidden State ════#
		this.subelements.modal.removeAttribute("hidden");
		this.setAttribute(MenuElement.openAttribute, "");


		// #════ Soft Dismiss ════#
		this.addSoftDismiss();


		// #════ Backdrop ════#
		document.documentElement.setAttribute("data-backdrop", "");
	}


	// #=#=#=#=#=# 1.? Event Close #=#=#=#=#=#
	/**
	 * Closes the Dialog
	 */
	public close() {
		// #════ Add Hidden State ════#
		this.subelements.modal.setAttribute("hidden", "");
		this.removeAttribute(MenuElement.openAttribute);


		// #════ Soft Dismiss ════#
		this.removeSoftDismiss();


		// #════ Backdrop ════#
		document.documentElement.removeAttribute("data-backdrop");
	}


	// #═#═#═#═#═# 1.? Add Softdismiss #═#═#═#═#═#
	/**
	 * Adds a dismiss function on ESC Key
	 */
	private addSoftDismiss(): void {
		// #════ ESC Key ════#
		document.addEventListener('keydown', this.softDismissESC);


		// #════ Backdrop ════#
		MenuElement.backdrop?.addEventListener('click', this.boundClose);
	}


	// #═#═#═#═#═# 1.? Remove Softdismiss #═#═#═#═#═#
	/**
	 * Removes the dismiss function on ESC Key
	 */
	private removeSoftDismiss(): void {
		// #════ ESC Key ════#
		document.removeEventListener('keydown', this.softDismissESC);


		// #════ Backdrop ════#
		MenuElement.backdrop?.removeEventListener('click', this.boundClose);
	}
}


// ==================================================
// MARK: 2. Initialisation
// --------------------------------------------------
window.customElements.define('cvh-menu', MenuElement);